import { FLAGS, LessonStatus, LIMITS } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { CATALOG_STALE_TIME, queryKeys, USER_DATA_STALE_TIME } from '@/lib/queryClient';
import {
  buildSections,
  buildUserLessonsMap,
  fetchCollections,
  fetchContinueLessonsForDashboard,
  fetchCourseThumbnailsCategories,
  fetchLatestCategory,
  fetchLatestLessons,
  fetchLinkedCourses,
  fetchSectionsWithLessons,
  fetchSkillPathwaysForDashboard,
  fetchUserCourseProgress,
  fetchUserLessonsForCourse,
} from '@/supabase/services/optimizedCourse';
import type { Section, UserLesson } from '@/types';
import { extractSimpleCourseIds } from '@/utils/courseHelpers';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Composite hook for dashboard course page data.
 * Fetches sections, collections, Latest, Continue watch, user progress.
 * Use only when course type is dashboard.
 */
export function useDashboardCourseData(
  courseId: number | undefined,
  userId: string | null | undefined,
  dashboardCourseSlug?: string,
  latestLimit: number = LIMITS.DEFAULT_LATEST,
  minStatus: number = LessonStatus.Published
) {
  const sectionsQuery = useQuery({
    queryKey: queryKeys.course.sections(courseId!, minStatus),
    queryFn: () => fetchSectionsWithLessons(courseId!, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const collectionsQuery = useQuery({
    queryKey: queryKeys.course.collections(courseId!),
    queryFn: () => fetchCollections(courseId!),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const courseThumbnailsQuery = useQuery({
    queryKey: [...queryKeys.course.courseThumbnailsCategories(courseId!), minStatus],
    queryFn: () => fetchCourseThumbnailsCategories(courseId!, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const latestLessonsQuery = useQuery({
    queryKey: queryKeys.course.latestLessons(courseId!, latestLimit, minStatus),
    queryFn: () => fetchLatestLessons(courseId!, latestLimit, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const latestCategoryQuery = useQuery({
    queryKey: ['course', courseId, 'latestCategory'],
    queryFn: async () => {
      try {
        return await fetchLatestCategory(courseId!);
      } catch {
        return null;
      }
    },
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const linkedCoursesQuery = useQuery({
    queryKey: ['course', courseId, 'linkedCourses', minStatus],
    queryFn: () => fetchLinkedCourses(courseId!, minStatus),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const linkedCourseIds = linkedCoursesQuery.data?.courseIds ?? [];
  const linkedCourseSlugsMap = linkedCoursesQuery.data?.slugsByCategoryId ?? {};

  const allCourseIdsForProgress = useMemo(
    () => (courseId ? [courseId, ...linkedCourseIds] : []),
    [courseId, linkedCourseIds]
  );

  const userLessonsQuery = useQuery({
    queryKey: [...queryKeys.userLessons.byCourse(userId || null, courseId!), linkedCourseIds],
    queryFn: async () => {
      logger.debug('[Cache] Fetching user lessons (no cache):', { userId, courseId, linkedCourseIds });
      return fetchUserLessonsForCourse(userId || null, allCourseIdsForProgress, false);
    },
    staleTime: USER_DATA_STALE_TIME,
    enabled: !!courseId,
    gcTime: 1 * 60 * 1000,
  });

  const simpleCourseIds = useMemo(
    () => extractSimpleCourseIds(courseThumbnailsQuery.data),
    [courseThumbnailsQuery.data]
  );

  const continueLessonsQuery = useQuery({
    queryKey: queryKeys.userLessons.continueForDashboard(userId || null, courseId!, [
      ...simpleCourseIds,
      ...linkedCourseIds,
    ]),
    queryFn: () =>
      fetchContinueLessonsForDashboard(
        userId || null,
        courseId!,
        simpleCourseIds,
        minStatus,
        linkedCourseIds,
        false,
        FLAGS.CONTINUE_INCLUDE_ALL_COURSES
      ),
    staleTime: USER_DATA_STALE_TIME,
    enabled: !!courseId && !!userId,
    gcTime: 1 * 60 * 1000,
  });

  const courseProgressQuery = useQuery({
    queryKey: queryKeys.userLessons.courseProgress(userId || null, simpleCourseIds),
    queryFn: () => fetchUserCourseProgress(userId || null, simpleCourseIds),
    staleTime: USER_DATA_STALE_TIME,
    enabled: !!courseId && simpleCourseIds.length > 0,
  });

  const skillPathwaysQuery = useQuery({
    queryKey: queryKeys.course.skillPathways(courseId!),
    queryFn: () => fetchSkillPathwaysForDashboard(courseId!),
    staleTime: CATALOG_STALE_TIME,
    enabled: !!courseId,
  });

  const userLessonsMap: Record<number, UserLesson> = useMemo(
    () =>
      userLessonsQuery.data?.allUserLessons
        ? buildUserLessonsMap(userLessonsQuery.data.allUserLessons)
        : {},
    [userLessonsQuery.data?.allUserLessons]
  );

  const sections: Section[] = useMemo(() => {
    const categoriesWithLessons = sectionsQuery.data || [];
    const collections = collectionsQuery.data || [];
    const courseThumbnailsCategories = courseThumbnailsQuery.data || [];
    const latestCategory = latestCategoryQuery.data ?? null;
    const latestLessons = latestLessonsQuery.data || [];
    const continueLessons = continueLessonsQuery.data || [];

    if (!categoriesWithLessons.length && !collections.length && !courseThumbnailsCategories.length) {
      return [];
    }

    logger.debug('[Cache] Building sections...');
    return buildSections(
      categoriesWithLessons,
      collections,
      latestCategory,
      latestLessons,
      continueLessons,
      userLessonsMap,
      courseThumbnailsCategories,
      skillPathwaysQuery.data || [],
      dashboardCourseSlug ?? null
    );
  }, [
    sectionsQuery.data,
    collectionsQuery.data,
    courseThumbnailsQuery.data,
    latestCategoryQuery.data,
    latestLessonsQuery.data,
    continueLessonsQuery.data,
    userLessonsMap,
    skillPathwaysQuery.data,
  ]);

  const isLoading =
    sectionsQuery.isLoading ||
    collectionsQuery.isLoading ||
    courseThumbnailsQuery.isLoading ||
    latestLessonsQuery.isLoading ||
    latestCategoryQuery.isLoading ||
    linkedCoursesQuery.isLoading ||
    skillPathwaysQuery.isLoading;

  const error =
    sectionsQuery.error ||
    collectionsQuery.error ||
    courseThumbnailsQuery.error ||
    latestLessonsQuery.error ||
    continueLessonsQuery.error ||
    latestCategoryQuery.error ||
    linkedCoursesQuery.error ||
    userLessonsQuery.error ||
    skillPathwaysQuery.error ||
    courseProgressQuery.error;

  const courseProgressMap = courseProgressQuery.data ?? {};

  return {
    sections,
    userLessonsMap,
    courseProgressMap,
    simpleCourseIds,
    linkedCourseSlugsMap,
    isLoading,
    error,
    queries: {
      sectionsQuery,
      collectionsQuery,
      courseThumbnailsQuery,
      latestLessonsQuery,
      latestCategoryQuery,
      linkedCoursesQuery,
      userLessonsQuery,
      continueLessonsQuery,
      courseProgressQuery,
      skillPathwaysQuery,
    },
  };
}

